import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, Link, useParams, useNavigate } from 'react-router-dom'
import { FiRefreshCw } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { getInventoryHealth } from '@/lib/inventory/health'
import { getEtsyHealth } from '@/lib/etsy/health'
import { getEtsyShops, selectEtsyShop } from '@/lib/etsy/shops'
import { disconnectEtsy } from '@/lib/etsy/disconnect'
import {
  getPrintifyHealth,
  connectPrintifyAccount,
  disconnectPrintifyAccount,
} from '@/lib/printify/health'
import { getBrands } from '@/lib/queries/brands'
import { useAuthStore } from '@/store/authStore'
import { setUserSelectedBrandId } from '@/lib/queries/userPreferences'

export default function Settings() {
  const { brandId } = useParams<{ brandId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)

  // Handle OAuth callback status
  useEffect(() => {
    const etsyStatus = searchParams.get('etsy_status')
    if (etsyStatus === 'connected') {
      setSearchParams({}, { replace: true })
      queryClient.invalidateQueries({ queryKey: ['etsy', 'health', brandId || 'legacy'] })
    }
  }, [searchParams, setSearchParams, queryClient, brandId])

  const {
    data: health,
    isLoading: isHealthLoading,
  } = useQuery({
    queryKey: ['inventory', 'health'],
    queryFn: getInventoryHealth,
    staleTime: 60_000,
  })

  const {
    data: etsyHealth,
    isLoading: isEtsyHealthLoading,
  } = useQuery({
    queryKey: ['etsy', 'health', brandId || 'legacy'],
    queryFn: () => getEtsyHealth(brandId),
    // Always re-check Etsy connection when entering Settings for a brand.
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const etsyError = searchParams.get('etsy_error')
  const needsShopSelection = !!brandId && !isEtsyHealthLoading && etsyHealth?.reason === 'shop_not_selected'

  const {
    data: etsyShopsResult,
    isLoading: isEtsyShopsLoading,
  } = useQuery({
    queryKey: ['etsy', 'shops', brandId || 'legacy'],
    queryFn: () => getEtsyShops(brandId),
    enabled: needsShopSelection,
  })

  const etsyShopList = useMemo(() => etsyShopsResult?.shops ?? [], [etsyShopsResult?.shops])
  const shopsLoadMessage = etsyShopsResult?.fetchMessage

  const [selectedShopId, setSelectedShopId] = useState<string | null>(null)

  useEffect(() => {
    if (!needsShopSelection) return
    if (selectedShopId !== null) return
    if (etsyShopList.length === 0) return
    setSelectedShopId(String(etsyShopList[0].shopId))
  }, [needsShopSelection, selectedShopId, etsyShopList])

  const selectShopMutation = useMutation({
    mutationFn: async () => {
      if (!brandId) throw new Error('missing_brandId')
      if (!selectedShopId) throw new Error('missing_shopId')
      await selectEtsyShop(selectedShopId, brandId)
    },
    onSuccess: async () => {
      setSearchParams({}, { replace: true })
      queryClient.invalidateQueries({ queryKey: ['etsy', 'shops', brandId || 'legacy'] })
      setSelectedShopId(null)
      // Optimistically show Connected so UI updates immediately; then refetch to confirm.
      queryClient.setQueryData(['etsy', 'health', brandId || 'legacy'], {
        connected: true,
        provider: 'etsy',
      })
      await queryClient.refetchQueries({ queryKey: ['etsy', 'health', brandId || 'legacy'] })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectEtsy(brandId),
    onSuccess: async () => {
      // Clear cached shop list and force health so UI shows Connect (not Refresh/Disconnect).
      queryClient.removeQueries({ queryKey: ['etsy', 'shops', brandId || 'legacy'] })
      queryClient.setQueryData(['etsy', 'health', brandId || 'legacy'], {
        connected: false,
        provider: 'etsy',
        reason: 'missing_refresh_token',
      })
      await queryClient.refetchQueries({ queryKey: ['etsy', 'health', brandId || 'legacy'] })
    },
  })

  const {
    data: printifyHealth,
    isLoading: isPrintifyHealthLoading,
  } = useQuery({
    queryKey: ['printify', 'health', brandId || 'legacy'],
    queryFn: () => getPrintifyHealth(brandId),
    enabled: !!brandId,
    staleTime: 60_000,
  })

  const [showPrintifyAdd, setShowPrintifyAdd] = useState(false)
  const [printifyToken, setPrintifyToken] = useState('')

  const printifyConnectMutation = useMutation({
    mutationFn: ({ token, brandId: bid }: { token: string; brandId: string }) =>
      connectPrintifyAccount(token, bid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printify', 'health', brandId || 'legacy'] })
      setShowPrintifyAdd(false)
      setPrintifyToken('')
      toast.success('Printify account connected')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to connect')
    },
  })

  const printifyDisconnectMutation = useMutation({
    mutationFn: ({ brandId: bid }: { brandId: string }) => disconnectPrintifyAccount(bid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printify', 'health', brandId || 'legacy'] })
      toast.success('Printify account disconnected')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect')
    },
  })

  const commitHash = import.meta.env.VITE_COMMIT_SHA || 'dev'
  const frontendOrigin =
    typeof window !== 'undefined' && typeof window.location?.origin === 'string' ? window.location.origin : ''
  const frontendOriginParam = frontendOrigin ? `&frontendOrigin=${encodeURIComponent(frontendOrigin)}` : ''
  const { data: brands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    staleTime: 60_000,
  })

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900">Settings</h1>
        </div>
      </header>

      <main className="p-4 space-y-4">
        <section className="bg-white rounded-lg p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Brand</h2>
          <label htmlFor="settings-brand-switch" className="block text-sm font-medium text-gray-700 mb-1">
            Active brand
          </label>
          <select
            id="settings-brand-switch"
            className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base touch-target-min-44 bg-white"
            value={brandId || ''}
            onChange={async (e) => {
              const nextBrandId = e.target.value
              if (!nextBrandId || nextBrandId === brandId) return
              if (user?._id) {
                await setUserSelectedBrandId(user._id, nextBrandId)
              }
              navigate(`/brands/${nextBrandId}/settings`)
            }}
          >
            {!brandId && <option value="">Select a brand</option>}
            {brands.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name}
              </option>
            ))}
          </select>
        </section>

        <section className="bg-white rounded-lg p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Integrations</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Zoho Inventory</div>
              <div className="text-xs text-gray-600">
                Status:{' '}
                {isHealthLoading ? (
                  <span className="text-gray-500">Checking…</span>
                ) : health?.connected ? (
                  <span className="text-emerald-700">Connected</span>
                ) : (
                  <span className="text-red-700">Disconnected</span>
                )}
              </div>
              {!isHealthLoading && (
                <div className="mt-1 space-y-0.5 text-[11px] text-gray-600">
                  {health?.datacenter && (
                    <div>
                      Datacenter: <span className="font-medium uppercase">{health.datacenter}</span>
                    </div>
                  )}
                  {!health?.connected && health?.reason && (
                    <div>
                      Last error:{' '}
                      <span className="font-medium">
                        {health.reason === 'missing_credentials'
                          ? 'Server missing Zoho credentials'
                          : health.reason === 'token_exchange_failed'
                            ? 'Zoho rate limiting token refresh – try again later'
                            : health.reason === 'network_error'
                              ? 'Network error talking to Zoho'
                              : health.reason}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {isHealthLoading ? (
              <span className="text-xs text-gray-500">…</span>
            ) : health?.connected ? (
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs text-gray-500">OK</span>
                <a
                  href="/api/zoho/oauth/start"
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 underline hover:text-emerald-800"
                >
                  <FiRefreshCw aria-hidden="true" />
                  <span>Refresh connection</span>
                </a>
              </div>
            ) : (
              <a
                href="/api/zoho/oauth/start"
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44"
              >
                Connect
              </a>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Integrations</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Etsy</div>
                <div className="text-xs text-gray-600">
                  Status:{' '}
                  {isEtsyHealthLoading ? (
                    <span className="text-gray-500">Checking…</span>
                  ) : etsyHealth?.connected ? (
                    <span className="text-emerald-700">Connected</span>
                  ) : etsyHealth?.reason === 'shop_not_selected' ? (
                    <span className="text-amber-800 font-medium">Shop selection needed</span>
                  ) : (
                    <span className="text-red-700">Disconnected</span>
                  )}
                </div>
                {etsyError === 'wrong_shop' && !needsShopSelection && (
                  <div className="mt-1 text-[11px] text-red-700 font-medium text-right">
                    Etsy connection was for a different shop than your previously selected one.
                  </div>
                )}
                {!isEtsyHealthLoading && (
                  <div className="mt-1 space-y-0.5 text-[11px] text-gray-600">
                    {etsyHealth?.reason === 'shop_not_selected' && (
                      <div className="text-amber-900">
                        Etsy is authorized for this app — choose which shop applies to this brand (below).
                      </div>
                    )}
                    {!etsyHealth?.connected &&
                      etsyHealth?.reason &&
                      etsyHealth.reason !== 'shop_not_selected' && (
                        <div>
                          Last error:{' '}
                          <span className="font-medium">
                            {etsyHealth.reason === 'missing_credentials'
                              ? 'Server missing Etsy credentials'
                              : etsyHealth.reason === 'missing_refresh_token'
                                ? 'Etsy not connected'
                                : etsyHealth.reason === 'token_exchange_failed'
                                  ? 'Token refresh failed – try again later'
                                  : etsyHealth.reason === 'network_error'
                                    ? 'Network error talking to Etsy'
                                    : etsyHealth.reason}
                          </span>
                        </div>
                      )}
                  </div>
                )}
              </div>
              {isEtsyHealthLoading ? (
                <span className="text-xs text-gray-500">…</span>
              ) : needsShopSelection ? (
                <div className="flex flex-col items-stretch gap-2 w-full max-w-[280px] text-left">
                  <p className="text-[11px] text-gray-600 leading-snug">
                    One Etsy shop per brand. Select yours below, then <span className="font-medium">Save shop</span>{' '}
                    so imports and listings use the correct store.
                  </p>
                  {etsyError === 'wrong_shop' && (
                    <div className="text-[11px] text-red-700 font-medium">
                      Last connect targeted a different shop than saved for this brand. Pick the correct shop below.
                    </div>
                  )}

                  {isEtsyShopsLoading ? (
                    <span className="text-xs text-gray-500">Loading your Etsy shops…</span>
                  ) : etsyShopList.length === 0 ? (
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left space-y-2">
                      {shopsLoadMessage ? (
                        <p className="font-medium text-amber-950">Could not load shops from Etsy</p>
                      ) : (
                        <p className="font-medium text-amber-950">No shops listed for this Etsy account</p>
                      )}
                      {shopsLoadMessage && (
                        <p className="text-[10px] text-amber-950/90 break-words font-mono">{shopsLoadMessage}</p>
                      )}
                      {!shopsLoadMessage && (
                        <p>
                          The signed-in Etsy user may not have opened a shop yet. Open a shop on Etsy, or sign in with
                          the account that owns your shop, then try again.
                        </p>
                      )}
                      <p>
                        <a
                          href={`/api/etsy/oauth/start?redirect=${brandId ? `/brands/${brandId}/settings` : '/settings'}${
                            brandId ? `&brandId=${brandId}` : ''
                          }${frontendOriginParam}&forceLogin=1`}
                          className="font-medium text-amber-950 underline"
                        >
                          Re-authorize with Etsy
                        </a>{' '}
                        — may keep the same Etsy browser session. To sign in as a different person, use{' '}
                        <span className="font-medium">Disconnect</span> below, then Connect, or log out of Etsy in
                        this browser first.
                      </p>
                      <div className="pt-2 border-t border-amber-200 space-y-2">
                        <label htmlFor="etsy-manual-shop-id" className="block text-[11px] font-medium text-amber-950">
                          Enter Etsy shop ID manually
                        </label>
                        <input
                          id="etsy-manual-shop-id"
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 57422307"
                          value={selectedShopId ?? ''}
                          onChange={(e) => setSelectedShopId(e.target.value.trim())}
                          className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
                          disabled={selectShopMutation.isPending}
                        />
                        <button
                          type="button"
                          onClick={() => selectShopMutation.mutate()}
                          disabled={!selectedShopId || selectShopMutation.isPending}
                          className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
                        >
                          {selectShopMutation.isPending ? 'Saving…' : 'Save shop ID'}
                        </button>
                        <p className="text-[10px] text-amber-950/80">
                          Use this if Etsy returns zero shops but you know your shop ID.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <label htmlFor="etsy-shop-select" className="sr-only">
                        Etsy shop for this brand
                      </label>
                      <select
                        id="etsy-shop-select"
                        value={selectedShopId ?? ''}
                        onChange={(e) => setSelectedShopId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm touch-target-min-44"
                        disabled={selectShopMutation.isPending}
                      >
                        {etsyShopList.map((shop) => (
                          <option key={shop.shopId} value={shop.shopId}>
                            {shop.shopName || shop.shopId}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => selectShopMutation.mutate()}
                        disabled={!selectedShopId || selectShopMutation.isPending}
                        className="w-full bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
                      >
                        {selectShopMutation.isPending ? 'Saving…' : 'Save shop'}
                      </button>
                    </>
                  )}
                  <div className="pt-2 border-t border-gray-200 space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            'Disconnect Etsy for this brand? Tokens for this app will be removed. Use this to sign in with a different Etsy account.',
                          )
                        ) {
                          disconnectMutation.mutate()
                        }
                      }}
                      disabled={disconnectMutation.isPending}
                      className="w-full text-sm text-red-700 font-medium underline hover:text-red-800 disabled:opacity-50 touch-target-min-44 text-left"
                    >
                      {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect Etsy'}
                    </button>
                    <p className="text-[10px] text-gray-500 leading-snug">
                      “Re-authorize” does not clear our tokens. Disconnect if you need a different Etsy login.
                    </p>
                  </div>
                </div>
              ) : etsyHealth?.connected ? (
                <div className="flex flex-col items-end gap-2">
                  <span className="text-xs text-gray-500">OK</span>
                  <a
                    href={`/api/etsy/oauth/start?redirect=${brandId ? `/brands/${brandId}/settings` : '/settings'}${
                      brandId ? `&brandId=${brandId}` : ''
                    }${frontendOriginParam}&forceLogin=1`}
                    className="inline-flex items-center gap-1 text-xs text-emerald-700 underline hover:text-emerald-800"
                  >
                    <FiRefreshCw aria-hidden="true" />
                    <span>Refresh connection</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Disconnect Etsy for this app? Local tokens will be removed; you must connect again to import or sync listings.',
                        )
                      ) {
                        disconnectMutation.mutate()
                      }
                    }}
                    disabled={disconnectMutation.isPending}
                    className="text-xs text-red-700 font-medium underline hover:text-red-800 disabled:opacity-50 touch-target-min-44"
                  >
                    {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              ) : (
                <a
                  href={`/api/etsy/oauth/start?redirect=${brandId ? `/brands/${brandId}/settings` : '/settings'}${
                    brandId ? `&brandId=${brandId}` : ''
                  }${frontendOriginParam}&forceLogin=1`}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44"
                >
                  Connect
                </a>
              )}
            </div>
            <div className="pt-2 border-t border-gray-200">
              <Link
                to={brandId ? `/brands/${brandId}/integrations/etsy` : '/integrations/etsy'}
                className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 text-center hover:bg-blue-700 transition-colors"
              >
                Manage Etsy Config Sets
              </Link>
              {brandId && etsyHealth?.connected && (
                <Link
                  to={`/brands/${brandId}/integrations/etsy/products`}
                  className="mt-2 block w-full bg-sky-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 text-center hover:bg-sky-700 transition-colors"
                >
                  View Etsy products
                </Link>
              )}
              {brandId && etsyHealth?.connected && (
                <Link
                  to={`/brands/${brandId}/integrations/etsy/orders`}
                  className="mt-2 block w-full bg-sky-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 text-center hover:bg-sky-700 transition-colors"
                >
                  View Etsy orders
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Printify</h2>
          <p className="text-xs text-gray-600 mb-3">
            One Printify API token per brand, stored in the worker (not in Sanity).
          </p>
          <div className="space-y-4">
            {!brandId ? (
              <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-medium mb-2">Choose a brand first</p>
                <p className="text-xs text-amber-950/90 mb-3">
                  Open this screen from the Dashboard with a brand selected, or use the brand picker, so we can store
                  this token for the correct brand.
                </p>
                <Link
                  to="/brands/picker"
                  className="inline-block bg-amber-800 text-white px-4 py-2 rounded-lg text-sm font-medium touch-target-min-44"
                >
                  Pick a brand
                </Link>
              </div>
            ) : isPrintifyHealthLoading ? (
              <div className="text-xs text-gray-500">Checking…</div>
            ) : (
              <>
                {(printifyHealth?.accounts ?? []).map((acc) => (
                  <div
                    key={acc.brandId}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <div className="font-medium">
                        {acc.firstPrintifyShopTitle?.trim() || 'Printify'}
                      </div>
                      <div className="text-xs text-gray-600">
                        {acc.status === 'connected' ? (
                          <span className="text-emerald-700">
                            Connected • {acc.printifyShopCount ?? 0} shop(s)
                          </span>
                        ) : (
                          <span className="text-red-700">Error</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Disconnect Printify for this brand?')) {
                          printifyDisconnectMutation.mutate({ brandId: acc.brandId })
                        }
                      }}
                      disabled={printifyDisconnectMutation.isPending}
                      className="text-xs text-red-600 underline hover:text-red-700 touch-target-min-44"
                    >
                      Disconnect
                    </button>
                  </div>
                ))}
                {brandId &&
                  (printifyHealth?.accounts ?? []).some((a) => a.status === 'connected' && a.brandId === brandId) && (
                    <div className="pt-2 border-t border-gray-200">
                      <Link
                        to={`/brands/${brandId}/integrations/printify`}
                        className="block w-full bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 text-center hover:bg-emerald-700 transition-colors"
                      >
                        View Printify products
                      </Link>
                    </div>
                  )}
                {(printifyHealth?.accounts ?? []).length === 0 && showPrintifyAdd ? (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                    <div>
                      <label
                        htmlFor="printify-api-token"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        API token
                      </label>
                      <input
                        id="printify-api-token"
                        type="password"
                        value={printifyToken}
                        onChange={(e) => setPrintifyToken(e.target.value)}
                        placeholder="Paste your Printify token"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Create at{' '}
                        <a
                          href="https://printify.com/app/account/api"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          printify.com/app/account/api
                        </a>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!brandId) return
                          printifyConnectMutation.mutate({
                            token: printifyToken,
                            brandId,
                          })
                        }}
                        disabled={!printifyToken.trim() || printifyConnectMutation.isPending}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        Connect
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPrintifyAdd(false)
                          setPrintifyToken('')
                        }}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (printifyHealth?.accounts ?? []).length === 0 ? (
                  <button
                    type="button"
                    onClick={() => setShowPrintifyAdd(true)}
                    className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 text-center hover:bg-emerald-700 transition-colors"
                  >
                    Add Printify account
                  </button>
                ) : null}
              </>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">App info</h2>
          <div className="text-xs text-gray-600">
            <div>
              App version (commit):
              <span className="ml-1 font-mono text-[11px] break-all">{commitHash}</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

